/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Helper function to handle standardized API responses
 * This function handles error checking and data extraction consistently
 * @param response Fetch Response object
 * @returns The data property from the ApiResponse
 * @throws Error with message from the API if the request failed
 */
export async function handleApiResponse<T>(response: Response): Promise<T> {
  // Check if the response is OK (status 200-299)
  if (!response.ok) {
    // Try to parse the error response to extract user-friendly message
    let errorMessage = `Request failed (${response.status})`;
    
    try {
      const errorData = await response.json();
      // Check if the response follows our standard error format
      if (!errorData.success && errorData.error) {
        errorMessage = errorData.error.message || errorMessage;
      }
    } catch {
      // JSON parsing failed - use the default error message
    }
    
    throw new Error(errorMessage);
  }

  // Parse the response as JSON
  const apiResponse = await response.json();

  // Verify the response follows our standard format
  if (!apiResponse.success) {
    throw new Error(apiResponse.error?.message || 'Request failed');
  }

  // Return just the data portion of the response
  return apiResponse.data as T;
} 